import { z } from "zod";
import { SignalKindSchema } from "./signals.js";
/**
 * Activation Predicate DSL.
 *
 * The decision the engine makes here is "does this user fit this persona's
 * activation criteria right now?". It mirrors the outcome-strategy
 * predicate pattern in `outcomeStrategy.ts` but operates over a different
 * context (signals, conditions, medications, clinical markers) and has a
 * different evaluator. Two DSLs is deliberate: outcome predicates gate
 * "is the math honest yet?"; activation predicates gate "does this lens
 * apply?". Combining them would tangle two unrelated concerns.
 *
 * Design choices:
 *   - Discriminated union keyed on `kind`. Zod auto-narrows in the
 *     evaluator switch.
 *   - Composition primitives (all_of/any_of/not) are recursive via
 *     z.lazy(). Recursion-depth is guarded in the evaluator.
 *   - Domain semantics carry typed inputs (SignalKindSchema, marker
 *     enum). JSON Logic was considered and rejected: too generic, lost
 *     too much type safety on the leaf predicates.
 *   - Failing closed. When required context is missing (no profile,
 *     no signals), predicates return ok=false with a reason. Silent
 *     mismatches are far worse than explicit no-data outcomes.
 */
// ---------------------------------------------------------------------------
// Leaf predicates - domain-specific, typed inputs.
// ---------------------------------------------------------------------------
const ConditionPresentPredicate = z
    .object({
    kind: z.literal("condition_present"),
    /** Free-text condition slug or label, matched case-insensitively against
     *  the user's conditions list. */
    condition: z.string().min(1).max(120),
})
    .strict();
const SignalActiveRecentlyPredicate = z
    .object({
    kind: z.literal("signal_active_recently"),
    signalKind: SignalKindSchema,
    /** Lookback window in days. Required - no implicit default to keep
     *  predicates self-describing. */
    withinDays: z.number().int().positive().max(365),
    /** Minimum count of signals of that kind in the window. Default 1
     *  (any single occurrence). */
    minOccurrences: z.number().int().min(1).max(10000).default(1),
})
    .strict();
const MedicationInClassPredicate = z
    .object({
    kind: z.literal("medication_in_class"),
    /** Class slug ('glp1_agonist', 'statin', 'ssri', etc). Matched
     *  case-insensitively against medication.class on the user. */
    medicationClass: z.string().min(1).max(60),
})
    .strict();
/** Clinical markers we currently support thresholding on. Mirrors the
 *  fields on PersonaContext.clinicalMarkers - keeping these aligned
 *  prevents predicates that reference markers we don't actually capture. */
const ClinicalMarkerSchema = z.enum(["a1c", "weightKg"]);
const ClinicalMarkerAbovePredicate = z
    .object({
    kind: z.literal("clinical_marker_above"),
    marker: ClinicalMarkerSchema,
    threshold: z.number(),
})
    .strict();
const ClinicalMarkerBelowPredicate = z
    .object({
    kind: z.literal("clinical_marker_below"),
    marker: ClinicalMarkerSchema,
    threshold: z.number(),
})
    .strict();
// ---------------------------------------------------------------------------
// Engine-state primitives (Spec 4 of the Heartbeat overhaul).
//
// These extend the activation DSL beyond medical-state predicates so that
// the new lens stack (Receptivity, Habituation, Verification) can use the
// SAME predicate evaluator the persona resolver already uses.
//
// Each new primitive references a field on ActivationContext that's
// optional and populated by the LENS caller, not the persona resolver.
// When the field is missing, predicates fail closed (return ok=false with
// reason "context-not-populated"). That means a persona manifest using one
// of these primitives without the lens infrastructure simply doesn't
// activate, rather than activating on a phantom-true.
// ---------------------------------------------------------------------------
const IdleForMinutesPredicate = z
    .object({
    kind: z.literal("idle_for_minutes"),
    /** Pass when the user has been idle (no foreground activity) for at
     *  least this many minutes. */
    atLeast: z.number().int().min(0).max(1440),
})
    .strict();
const LastForegroundWithinPredicate = z
    .object({
    kind: z.literal("last_foreground_within"),
    /** Pass when the user had the app in foreground within the last N
     *  minutes. Used to detect "user is here right now". */
    minutes: z.number().int().min(0).max(1440),
})
    .strict();
const DuringWindowPredicate = z
    .object({
    kind: z.literal("during_window"),
    /** Window start in minutes-since-local-midnight. e.g. 06:00 = 360. */
    startMinute: z.number().int().min(0).max(1439),
    /** Window end in minutes-since-local-midnight, exclusive. e.g. 11:00 = 660.
     *  Wrapping windows (start > end) are allowed - "21:00 to 06:00" wraps
     *  midnight; the evaluator handles both wrapping and non-wrapping. */
    endMinute: z.number().int().min(0).max(1439),
})
    .strict();
const LastDismissedMinutesAgoPredicate = z
    .object({
    kind: z.literal("last_dismissed_minutes_ago"),
    /** Pass when the most recent dismissal was at least this many minutes
     *  ago. If the user has never dismissed, returns ok (no cooldown to
     *  wait for). */
    atLeast: z.number().int().min(0).max(1440),
})
    .strict();
const SurfaceCountTodayPredicate = z
    .object({
    kind: z.literal("surface_count_today"),
    /** Pass when surfaces fired today is at most this. Used as a daily
     *  attention budget. */
    atMost: z.number().int().min(0).max(100),
})
    .strict();
const FamilyCountInWindowPredicate = z
    .object({
    kind: z.literal("family_count_in_window"),
    /** Family slug, e.g. "microstep.movement", "library.reset". */
    family: z.string().min(1).max(60),
    /** Lookback window in days. */
    windowDays: z.number().int().positive().max(90),
    /** Pass when the family has fired at most this many times in the
     *  window. The Habituation lens reads `lastFiredAt` to compute counts. */
    atMost: z.number().int().min(0).max(100),
})
    .strict();
const LastVerdictForPredicate = z
    .object({
    kind: z.literal("last_verdict_for"),
    /** Predicted-outcome metric (signal kind name), e.g. "wearable.sleep". */
    metric: z.string().min(1).max(60),
    equals: z.enum(["confirmed", "no-effect", "inconclusive", "insufficient-data"]),
})
    .strict();
// Zod 4: recursive discriminated unions need z.lazy() on the recursive
// REFERENCE (inside the array / nested field), not on the outer schema.
// Wrapping the whole discriminatedUnion in z.lazy() breaks discriminator
// detection because the union loses direct access to its arms.
export const ActivationPredicateSchema = z.discriminatedUnion("kind", [
    ConditionPresentPredicate,
    SignalActiveRecentlyPredicate,
    MedicationInClassPredicate,
    ClinicalMarkerAbovePredicate,
    ClinicalMarkerBelowPredicate,
    IdleForMinutesPredicate,
    LastForegroundWithinPredicate,
    DuringWindowPredicate,
    LastDismissedMinutesAgoPredicate,
    SurfaceCountTodayPredicate,
    FamilyCountInWindowPredicate,
    LastVerdictForPredicate,
    z
        .object({
        kind: z.literal("all_of"),
        predicates: z
            .array(z.lazy(() => ActivationPredicateSchema))
            .min(1)
            .max(20),
    })
        .strict(),
    z
        .object({
        kind: z.literal("any_of"),
        predicates: z
            .array(z.lazy(() => ActivationPredicateSchema))
            .min(1)
            .max(20),
    })
        .strict(),
    z
        .object({
        kind: z.literal("not"),
        predicate: z.lazy(() => ActivationPredicateSchema),
    })
        .strict(),
]);
/**
 * Recursion-depth ceiling. 8 levels is generous for any realistic
 * predicate tree (a 3-condition all_of nested inside an any_of inside a
 * not is depth 4). Failing closed beyond the ceiling prevents pathological
 * configs from locking the evaluator.
 */
const MAX_DEPTH = 8;
export function evaluateActivation(predicate, ctx) {
    return evaluateInner(predicate, ctx, 0);
}
function evaluateInner(predicate, ctx, depth) {
    if (depth > MAX_DEPTH) {
        return { ok: false, reason: `predicate nesting depth exceeded (>${MAX_DEPTH})` };
    }
    switch (predicate.kind) {
        case "condition_present": {
            const target = predicate.condition.toLowerCase();
            const hit = ctx.conditions.some((c) => c.toLowerCase() === target);
            return hit
                ? { ok: true }
                : { ok: false, reason: `condition not present: ${predicate.condition}` };
        }
        case "signal_active_recently": {
            const cutoff = new Date(ctx.now.getTime() - predicate.withinDays * 86_400_000);
            const min = predicate.minOccurrences ?? 1;
            const count = ctx.signals.filter((s) => s.kind === predicate.signalKind && s.occurredAt >= cutoff).length;
            return count >= min
                ? { ok: true }
                : {
                    ok: false,
                    reason: `signal ${predicate.signalKind} occurred ${count} times in last ${predicate.withinDays}d (need ${min})`,
                };
        }
        case "medication_in_class": {
            const target = predicate.medicationClass.toLowerCase();
            const hit = ctx.medications.some((m) => m.class?.toLowerCase() === target);
            return hit
                ? { ok: true }
                : { ok: false, reason: `no medication in class ${predicate.medicationClass}` };
        }
        case "clinical_marker_above": {
            const value = ctx.clinicalMarkers[predicate.marker];
            if (value === undefined) {
                return {
                    ok: false,
                    reason: `clinical marker ${predicate.marker} not captured`,
                    noData: true,
                };
            }
            return value > predicate.threshold
                ? { ok: true }
                : {
                    ok: false,
                    reason: `${predicate.marker}=${value} not above ${predicate.threshold}`,
                };
        }
        case "clinical_marker_below": {
            const value = ctx.clinicalMarkers[predicate.marker];
            if (value === undefined) {
                return {
                    ok: false,
                    reason: `clinical marker ${predicate.marker} not captured`,
                    noData: true,
                };
            }
            return value < predicate.threshold
                ? { ok: true }
                : {
                    ok: false,
                    reason: `${predicate.marker}=${value} not below ${predicate.threshold}`,
                };
        }
        case "idle_for_minutes": {
            if (ctx.idleForMinutes === undefined) {
                return { ok: false, reason: "idleForMinutes not populated", noData: true };
            }
            return ctx.idleForMinutes >= predicate.atLeast
                ? { ok: true }
                : {
                    ok: false,
                    reason: `idleForMinutes=${ctx.idleForMinutes} not at least ${predicate.atLeast}`,
                };
        }
        case "last_foreground_within": {
            // undefined = context not populated (no data); null = populated as
            // "never foregrounded", which is a definitive miss.
            if (ctx.lastForegroundAt === undefined) {
                return { ok: false, reason: "lastForegroundAt not populated", noData: true };
            }
            if (ctx.lastForegroundAt === null) {
                return { ok: false, reason: "app never in foreground" };
            }
            const ageMinutes = (ctx.now.getTime() - ctx.lastForegroundAt.getTime()) / 60_000;
            return ageMinutes <= predicate.minutes
                ? { ok: true }
                : {
                    ok: false,
                    reason: `last foreground ${Math.round(ageMinutes)}m ago, > ${predicate.minutes}m`,
                };
        }
        case "during_window": {
            if (ctx.nowMinutesSinceLocalMidnight === undefined) {
                return { ok: false, reason: "nowMinutesSinceLocalMidnight not populated", noData: true };
            }
            const m = ctx.nowMinutesSinceLocalMidnight;
            const wraps = predicate.startMinute > predicate.endMinute;
            const inside = wraps
                ? m >= predicate.startMinute || m < predicate.endMinute
                : m >= predicate.startMinute && m < predicate.endMinute;
            return inside
                ? { ok: true }
                : {
                    ok: false,
                    reason: `local minute ${m} not in [${predicate.startMinute}, ${predicate.endMinute})`,
                };
        }
        case "last_dismissed_minutes_ago": {
            // Never-dismissed = no cooldown to wait for; pass.
            if (ctx.lastDismissedAt === undefined || ctx.lastDismissedAt === null) {
                return { ok: true };
            }
            const ageMinutes = (ctx.now.getTime() - ctx.lastDismissedAt.getTime()) / 60_000;
            return ageMinutes >= predicate.atLeast
                ? { ok: true }
                : {
                    ok: false,
                    reason: `last dismissed ${Math.round(ageMinutes)}m ago, < ${predicate.atLeast}m cooldown`,
                };
        }
        case "surface_count_today": {
            if (ctx.surfacesToday === undefined) {
                return { ok: false, reason: "surfacesToday not populated", noData: true };
            }
            return ctx.surfacesToday <= predicate.atMost
                ? { ok: true }
                : {
                    ok: false,
                    reason: `surfacesToday=${ctx.surfacesToday} exceeds cap ${predicate.atMost}`,
                };
        }
        case "family_count_in_window": {
            if (!ctx.familyCountsInWindow) {
                return { ok: false, reason: "familyCountsInWindow not populated", noData: true };
            }
            const count = ctx.familyCountsInWindow.get(predicate.family) ?? 0;
            return count <= predicate.atMost
                ? { ok: true }
                : {
                    ok: false,
                    reason: `family ${predicate.family} fired ${count} times, exceeds cap ${predicate.atMost}`,
                };
        }
        case "last_verdict_for": {
            if (!ctx.verdictsByMetric) {
                return { ok: false, reason: "verdictsByMetric not populated", noData: true };
            }
            const verdict = ctx.verdictsByMetric.get(predicate.metric);
            if (verdict === undefined) {
                return { ok: false, reason: `no verdict for metric ${predicate.metric}`, noData: true };
            }
            return verdict === predicate.equals
                ? { ok: true }
                : {
                    ok: false,
                    reason: `verdict for ${predicate.metric} is "${verdict}", not "${predicate.equals}"`,
                };
        }
        case "all_of": {
            for (const p of predicate.predicates) {
                const r = evaluateInner(p, ctx, depth + 1);
                if (!r.ok) {
                    return {
                        ok: false,
                        reason: `all_of failed: ${r.reason}`,
                        ...(r.noData && { noData: true }),
                    };
                }
            }
            return { ok: true };
        }
        case "any_of": {
            const reasons = [];
            let anyNoData = false;
            for (const p of predicate.predicates) {
                const r = evaluateInner(p, ctx, depth + 1);
                if (r.ok)
                    return { ok: true };
                if (r.reason)
                    reasons.push(r.reason);
                if (r.noData)
                    anyNoData = true;
            }
            return {
                ok: false,
                reason: `any_of: none matched (${reasons.join("; ")})`,
                ...(anyNoData && { noData: true }),
            };
        }
        case "not": {
            const r = evaluateInner(predicate.predicate, ctx, depth + 1);
            if (r.ok)
                return { ok: false, reason: `not: inner predicate matched` };
            // Missing context makes the inner result unknown, not false; inverting
            // an unknown would turn fail-closed no-data into a phantom activation.
            if (r.noData) {
                return {
                    ok: false,
                    reason: `not: inner predicate lacked data (${r.reason})`,
                    noData: true,
                };
            }
            return { ok: true };
        }
    }
}
//# sourceMappingURL=personaActivation.js.map